import React, { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Row, Col, Card, Button, Collapse, CardBody } from "reactstrap";
import { ChevronUp, ChevronDown } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import InfoCard, { MobileInfoCard } from "./InfoCard";
import { useAppData } from "../context";
import { useAppQuery } from "../hooks/useAppQuery";
import { getSiteMetrics } from "../utils/getSiteMetrics";
import { BatteryChart, PricesChart } from "./graphs/Graphs";
import { useTranslation } from "react-i18next";
import batterySvg from "../assets/battery_icon.svg";
import electricalPylonSvg from "../assets/electrical_pylon_icon.svg"

const formatRelativeTime = (timestamp) =>
  timestamp
    ? formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    : "--";

function calculateFontSize(text) {
  const len = text.length;
  const baseSize = 2;
  const clampLen = 4;
  const minSize = 0.75;

  let size = (baseSize * clampLen) / len;
  size = Math.min(size, baseSize);
  size = Math.max(size, minSize);

  return `${size.toFixed(2)}rem`;
}

function getMetricSum(metricsGroup, keys) {
  // Extract numeric values safely
  const values = keys
    .map(key => metricsGroup?.[key]?.value)
    .filter(v => v != null); // keep only defined/non-null values

  if (values.length === 0) {
    // none of the keys had values
    return "N/A";
  }

  // sum them
  return values.reduce((sum, v) => sum + v, 0);
}


function getStyledValue(value, fallback = "N/A") {
  const text = value != null ? String(value) : fallback;
  const fontSize = calculateFontSize(text);

  return (
    <span style={{ display: "inline-block", fontSize, lineHeight: 1 }}>
      {text}
    </span>
  );
}

const SiteOverviewBlock = ({ siteId }) => {
  const data = useAppData();
  const [isOpen, setIsOpen] = useState(false);
  const { sites } = useAppData();
  const site = sites.find((s) => s.id === siteId);
  const { t } = useTranslation();
  const timeout = parseInt(window.appConfig?.getTimeout);
  const { data: parsedMetrics = {} } = useAppQuery(
    ["metrics", site.id],
    () => getSiteMetrics(site.id, timeout),
    {
      refetchInterval: 60000,
      structuralSharing: false,
      enabled: !!site?.id && !!timeout,
    }
  );

  const { latestMetrics: metrics = {}, twentyMinuteMetrics = {} } =
    parsedMetrics;

  const scheduleData = site.schedule ? site.schedule : [];
  const entsoeData = site.prices ? site.prices : [];
  const co2Data = site.carbon_intensity ? site.carbon_intensity : [];
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <Col className="px-0">
      <Card className="mb-3 shadow border-lt">
        <CardBody className="p-2 cardbody-md-default">
          <div
            className="d-flex flex-row justify-content-between align-items-center"
            style={{ minHeight: 60 }}
          >
            <div className="flex-fill text-center">
              {isMobile ? (
                // MOBILE VIEW
                <Row
                  data-testid="collapsed-row"
                  className="align-items-stretch g-0"
                  style={{ margin: 0 }}
                >
                  {!isOpen ? (
                    <>
                      <Col xs={6} className="d-flex">
                        <MobileInfoCard
                          label={t("SiteHomeSection-Grid")}
                          units="(W)"
                          value={getMetricSum(metrics.A01, ['Q01', 'Q06', 'Q07'])}
                          defaultValue="N/A"
                          valueFontSize="inherit"
                          subLabel={formatRelativeTime(metrics.A01?.Q01?.time)}
                          sparklineData={twentyMinuteMetrics.A01?.Q01}
                        />
                      </Col>
                      <Col xs={6} className="d-flex">
                        <MobileInfoCard
                          label={t("SiteHomeSection-Battery Charge")}
                          units="(%)"
                          value={(metrics.A14?.Q04?.value)}
                          defaultValue="N/A"
                          valueFontSize="inherit"
                          subLabel={formatRelativeTime(metrics.A14?.Q04?.time)}
                          sparklineData={twentyMinuteMetrics.A14?.Q04}
                        />
                      </Col>
                    </>
                  ) : (
                    <Col sm={12}>
                      <h4 className="mb-3 text-left text-muted">
                        <strong>{site.display_name}</strong>
                      </h4>
                    </Col>
                  )}
                </Row>
              ) : (
                //  DESKTOP VIEW
                <Row data-testid="collapsed-row">
                  {!isOpen ? (
                    <>
                      <Col sm={6}>
                        <InfoCard
                          label={t("SiteHomeSection-Grid")}
                          units="(W)"
                          value={getStyledValue(getMetricSum(metrics.A01, ['Q01', 'Q06', 'Q07']))}
                          defaultValue="N/A"
                          valueFontSize="inherit"
                          subLabel={formatRelativeTime(metrics.A01?.Q01?.time)}
                          sparklineData={twentyMinuteMetrics.A01?.Q01}
                          selectedImagePath={electricalPylonSvg}
                          imageDesc="Grid"
                        />
                      </Col>
                      <Col sm={6}>
                        <InfoCard
                          label={t("SiteHomeSection-Battery Charge")}
                          units="(%)"
                          value={getStyledValue(metrics.A14?.Q04?.value)}
                          defaultValue="N/A"
                          valueFontSize="inherit"
                          subLabel={formatRelativeTime(metrics.A14?.Q04?.time)}
                          sparklineData={twentyMinuteMetrics.A14?.Q04}
                          selectedImagePath={batterySvg}
                          imageDesc="Battery"
                        />
                      </Col>
                    </>
                  ) : (
                    <Col sm={12}>
                      <h4 className="mb-3 text-left text-muted">
                        <strong>{site.display_name}</strong>
                      </h4>
                    </Col>
                  )}
                </Row>
              )}
            </div>

            {/* Chevron button */}
            <div
              className="status-cell status-cell-4 align-self-start flex-shrink-0 ms-2"
              style={{ minHeight: 60 }}
            >
              <Button
                color="link"
                onClick={() => setIsOpen(!isOpen)}
                className="status-chevron"
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronUp data-testid="chevron-up" size={18} />
                ) : (
                  <ChevronDown data-testid="chevron-down" size={18} />
                )}
              </Button>
            </div>
          </div>
          <Collapse isOpen={isOpen}>
            {/* <CollapsibleSection */}
            <Row data-testid="expanded-row">
              <Col sm={4}>
                <InfoCard
                  label={t("SiteHomeSection-Load")}
                  units="(W)"
                  value={getStyledValue(getMetricSum(metrics.A12, ['Q01', 'Q06', 'Q07']))}
                  defaultValue="N/A"
                  valueFontSize="inherit"
                  subLabel={formatRelativeTime(metrics.A12?.Q01?.time)}
                  sparklineData={twentyMinuteMetrics.A12?.Q01}
                />
                <InfoCard
                  label={t("SiteHomeSection-Battery Charge")}
                  units="(%)"
                  value={getStyledValue(metrics.A14?.Q04?.value)}
                  defaultValue="N/A"
                  valueFontSize="inherit"
                  subLabel={formatRelativeTime(metrics.A14?.Q04?.time)}
                  sparklineData={twentyMinuteMetrics.A14?.Q04}
                />
              </Col>
              <Col sm={4}>
                <InfoCard
                  label={t("SiteHomeSection-PV")}
                  units="(W)"
                  value={getStyledValue(metrics.A13?.Q01?.value)}
                  defaultValue="N/A"
                  valueFontSize="inherit"
                  subLabel={formatRelativeTime(metrics.A13?.Q01?.time)}
                  sparklineData={twentyMinuteMetrics.A13?.Q01}
                />
                <InfoCard
                  label={t("SiteHomeSection-Battery Power")}
                  units="(W)"
                  value={getStyledValue(metrics.A14?.Q01?.value)}
                  defaultValue="N/A"
                  valueFontSize="inherit"
                  subLabel={formatRelativeTime(metrics.A14?.Q01?.time)}
                  sparklineData={twentyMinuteMetrics.A14?.Q01}
                />
              </Col>
              <Col sm={4}>
                <InfoCard
                  label={t("SiteHomeSection-Grid")}
                  units="(W)"
                  value={getStyledValue(getMetricSum(metrics.A01, ['Q01', 'Q06', 'Q07']))}
                  defaultValue="N/A"
                  valueFontSize="inherit"
                  subLabel={formatRelativeTime(metrics.A01?.Q01?.time)}
                  sparklineData={twentyMinuteMetrics.A01?.Q01}
                />
                <InfoCard
                  label={t("SiteHomeSection-Battery Set Point")}
                  units="(W)"
                  value={getStyledValue(metrics.A15?.Q05?.value)}
                  defaultValue="N/A"
                  valueFontSize="inherit"
                  subLabel={formatRelativeTime(metrics.A15?.Q05?.time)}
                  sparklineData={twentyMinuteMetrics.A15?.Q05}
                />
              </Col>
            </Row>
            <BatteryChart
              batteryPower={scheduleData}
              userTimezone={data.user.timezone}
            />
            <PricesChart
              prices={entsoeData}
              carbon_intensity={co2Data}
              userTimezone={data.user.timezone}
              chartType="line"
            />
          </Collapse>
        </CardBody>
      </Card>
    </Col>
  );
};

export default SiteOverviewBlock;
